import { logger } from '@gik/analytics/utils/logger';
import { useConfig } from '@gik/api/config/config';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useUserPages } from '@gik/api/inkinds/userPages';
import { useEmailAlreadyTaken } from '@gik/api/users/auth';
import { useUser } from '@gik/api/users/user';
import { auth } from '@gik/auth/auth';
import { useUserStore } from '@gik/core/store/UserStore';
import { FinishRegistration } from '@gik/finish-registration';
import {
  acceptInvitation,
  useInvitationEmailAddressFromToken,
} from '@gik/finish-registration/api/FinishRegistrationAPI';
import { finishRegistrationSearchParam, signupEmailSearchParam } from '@gik/finish-registration/consts';
import { UI } from '@gik/ui/UIManager';
import React from 'react';
import useSearchParam from 'react-use/lib/useSearchParam';

export default function useFinishRegistration(routeId: string) {
  const userId = useUserStore(state => state.id);
  const { data: user } = useUser(userId);
  const { data: inkindPage, mutate: mutateInkindPage } = useInkind(routeId);
  const { mutate: mutateConfig } = useConfig();

  const invitationToken = useSearchParam(finishRegistrationSearchParam);
  const signUpEmailAddress = useSearchParam(signupEmailSearchParam);

  const { data: { email: invitationEmailAddress } = { email: undefined } } =
    useInvitationEmailAddressFromToken(invitationToken);

  const { data: emailExistsStatus } = useEmailAlreadyTaken(invitationEmailAddress);
  const exist = emailExistsStatus?.exist || emailExistsStatus?.facebook || emailExistsStatus?.google;

  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const { mutate: mutateUserPages } = useUserPages(userId);

  const buttonsPortal = React.useRef<HTMLDivElement>();
  const debounceRef = React.useRef<NodeJS.Timeout>();
  const timeoutValue = 1200;

  function withDebounce(fn: () => void) {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fn();
    }, timeoutValue);
  }

  React.useEffect(() => {
    async function openFinishRegistrationModal() {
      if (!isOpen) {
        setIsOpen(true);
        await UI.dialog(<FinishRegistration routeId={routeId} buttonsPortal={buttonsPortal} />, {
          title: 'Finish Registration',
          closable: false,
          buttons: <div ref={buttonsPortal} />,
        });
      }
    }

    async function alreadyAcceptedInvitationAlert() {
      if (!isOpen) {
        setIsOpen(true);
        await UI.alert(
          `Thank you, you have already finished registering.${
            user ? '' : ' Please log in to your account to continue.'
          }`,
          {
            title: signUpEmailAddress ? 'Already registered' : 'Invitation already accepted',
          }
        );

        const url = new URL(window.location.toString());
        url.searchParams.delete(finishRegistrationSearchParam);
        url.searchParams.delete(signupEmailSearchParam);
        window.history.pushState({}, '', url.toString());
      }
    }

    async function openDifferentEmailLogOutConfirmDialog() {
      if (!isOpen) {
        setIsOpen(true);
        if (
          await UI.confirm(
            `You are accepting an invitation to register under email ${
              invitationEmailAddress ? invitationEmailAddress : '<>'
            } but are logged in with a different email (${
              user?.emailAddress || '<>'
            }). We will log you out so you can continue registering your new account.`,
            {
              title: 'Finish Registration',
            }
          )
        ) {
          auth.logout(false);
          window.location.reload();
        } else {
          const url = new URL(window.location.toString());
          url.searchParams.delete(finishRegistrationSearchParam);
          url.searchParams.delete(signupEmailSearchParam);
          window.history.pushState({}, '', url.toString());
        }
      }
    }

    withDebounce(() => {
      if (inkindPage) {
        if (signUpEmailAddress) {
          if (user) {
            if (user.emailAddress.toLowerCase() === invitationEmailAddress.toLowerCase()) {
              alreadyAcceptedInvitationAlert();
            } else {
              openDifferentEmailLogOutConfirmDialog();
            }
          } else if (!userId) {
            if (exist) {
              alreadyAcceptedInvitationAlert();
            } else {
              openFinishRegistrationModal();
            }
          }
        } else if (invitationToken && emailExistsStatus) {
          try {
            if (user) {
              if (user.emailAddress.toLowerCase() === invitationEmailAddress.toLowerCase()) {
                (async () => {
                  await acceptInvitation(inkindPage.routeId, invitationToken);

                  const url = new URL(window.location.toString());
                  url.searchParams.delete(finishRegistrationSearchParam);
                  window.history.pushState({}, '', url.toString());
                  mutateConfig();
                  mutateInkindPage();
                  mutateUserPages();
                })();
              } else {
                openDifferentEmailLogOutConfirmDialog();
              }
            } else if (!userId) {
              if (!exist) {
                openFinishRegistrationModal();
              } else {
                auth.signin();
              }
            }
          } catch (e) {
            logger.error('Failed to finish registration', e);
            alreadyAcceptedInvitationAlert();
          }
        }
      }
    });
  }, [
    inkindPage,
    user,
    userId,
    invitationToken,
    signUpEmailAddress,
    routeId,
    invitationEmailAddress,
    exist,
    mutateInkindPage,
    isOpen,
    emailExistsStatus,
    mutateUserPages,
    mutateConfig,
  ]);
}
